// src/pages/ViewPhotos.js (v5 – centralized media URLs, video src fixed, lightbox uses full image)

import React, { useState, useEffect, useRef } from "react";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import "../styles/ViewPhotos.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";

import api from "../services/api";
import { API_PATHS } from "../config";
import { getMediaUrls } from "../utils/media";
import timeAgo from "../utils/timeAgo";

// ---- Hashtag helpers -------------------------------------------------------

const normalizeSearchQuery = (query) =>
  query
    ? query.trim().toLowerCase().startsWith("#")
      ? query.trim().toLowerCase()
      : `#${query.trim().toLowerCase()}`
    : "";

const parseHashtag = (tag) => {
  const lower = (tag || "").toLowerCase();
  const match = /^([^-\s]+?)(?:-(\d{2})\/(\d{2}))?$/.exec(lower);
  if (!match) {
    return { base: lower, isYearVariant: false, yearStart: null, yearEnd: null };
  }
  const base = match[1];
  const yearStart = match[2] ? parseInt(match[2], 10) : null;
  const yearEnd = match[3] ? parseInt(match[3], 10) : null;
  return { base, isYearVariant: !!yearStart && !!yearEnd, yearStart, yearEnd };
};

const sortHashtagSuggestions = (items = [], query = "") => {
  if (!items.length) return [];
  const normQuery = normalizeSearchQuery(query);

  return [...items].sort((a, b) => {
    const tagA = a?.hashtag || "";
    const tagB = b?.hashtag || "";
    const infoA = parseHashtag(tagA);
    const infoB = parseHashtag(tagB);

    const aMatchesBase = normQuery && infoA.base === normQuery;
    const bMatchesBase = normQuery && infoB.base === normQuery;
    if (aMatchesBase !== bMatchesBase) return aMatchesBase ? -1 : 1;

    if (infoA.isYearVariant !== infoB.isYearVariant)
      return infoA.isYearVariant ? 1 : -1;

    if (infoA.isYearVariant && infoB.isYearVariant && infoA.base === infoB.base) {
      if (infoA.yearStart !== infoB.yearStart) return infoB.yearStart - infoA.yearStart;
    }

    if (infoA.base !== infoB.base) return infoA.base.localeCompare(infoB.base);
    return tagA.toLowerCase().localeCompare(tagB.toLowerCase());
  });
};

const MAX_VISIBLE_HASHTAGS = 20;

const ViewPhotos = ({ userRole, schoolName }) => {
  const [feedPhotos, setFeedPhotos] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMorePhotos, setHasMorePhotos] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const [hashtags, setHashtags] = useState([]);
  const [selectedHashtag, setSelectedHashtag] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showAllSchools, setShowAllSchools] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const observer = useRef(null);

  // Load photos (initial + infinite scroll + search)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const endpoint = selectedHashtag ? API_PATHS.HASHTAG_PHOTOS : API_PATHS.VIEW_PHOTOS;
        const params = {
          offset: currentPage * 20,
          limit: 20,
          ...(selectedHashtag && { hashtag: selectedHashtag }),
          all_schools: showAllSchools,
        };

        const { data } = await api.get(endpoint, { params });
        const newPhotos = Array.isArray(data) ? data : [];

        setFeedPhotos((prev) => (currentPage === 0 ? newPhotos : [...prev, ...newPhotos]));
        setHasMorePhotos(newPhotos.length === 20);
      } catch (err) {
        console.error("Error fetching photos:", err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [currentPage, selectedHashtag, showAllSchools]);

  // Hashtag autocomplete
  useEffect(() => {
    if (!searchQuery) {
      setHashtags([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const { data } = await api.get(API_PATHS.HASHTAGS_SEARCH, {
          params: { query: searchQuery, all_schools: showAllSchools },
        });

        const sorted = sortHashtagSuggestions(data || [], searchQuery);
        setHashtags(sorted);
      } catch (err) {
        console.error("Error fetching hashtags:", err);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, showAllSchools]);

  const handleHashtagClick = (hashtag) => {
    setSelectedHashtag(hashtag);
    setSearchQuery("");
    setFeedPhotos([]);
    setCurrentPage(0);
  };

  const handleClearSearch = () => {
    setSelectedHashtag(null);
    setSearchQuery("");
    setFeedPhotos([]);
    setCurrentPage(0);
  };

  const handleToggleSchools = () => {
    const newValue = !showAllSchools;
    setShowAllSchools(newValue);
    setFeedPhotos([]);
    setCurrentPage(0);
    setSelectedHashtag(null);
    setSearchQuery("");
  };

  // Back-to-top
  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Initial load check (role-based)
  useEffect(() => {
    if (!["PARENT", "STAFF", "PRINCIPAL"].includes(userRole)) return;
    setCurrentPage(0);
  }, [userRole]);

  // Infinite scroll observer
  const lastPhotoRef = (node) => {
    if (observer.current) observer.current.disconnect();
    if (!node || !hasMorePhotos || isLoading) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setCurrentPage((prev) => prev + 1);
        }
      },
      { threshold: 0.5 }
    );
    observer.current.observe(node);
  };

  const titleText = schoolName
    ? `${schoolName}'s Photos`
    : userRole === "PARENT"
      ? "Your School's Photos"
      : "Our School's Photos";

  const visibleHashtags = hashtags.slice(0, MAX_VISIBLE_HASHTAGS);
  const hiddenCount = Math.max(0, hashtags.length - MAX_VISIBLE_HASHTAGS);

  // Lightbox should only show non-video items
  const photosForLightbox = feedPhotos.filter((p) => String(p.media_type || "PHOTO") !== "VIDEO");

  return (
    <div className="view-photos-container page">
      <header className="view-photos-header">
        <div className="view-photos-heading">
          <h1 className="view-photos-title">{titleText}</h1>
          <p className="view-photos-subtitle">
            Browse your school&apos;s approved photos, filter by hashtags, and jump straight to the memories that matter.
          </p>
        </div>

        <div className="toggle-container">
          <label>
            <input type="checkbox" checked={showAllSchools} onChange={handleToggleSchools} />
            <span>Show photos from all schools</span>
          </label>
        </div>
      </header>

      <section className="view-photos-controls">
        <div className="search-wrapper">
          <div className="search-input-shell">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="search-input-icon" />
            <input
              type="text"
              placeholder="Search by hashtag… e.g. #FirstClass, #Juniors-24/25"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-bar"
            />
          </div>
        </div>

        {visibleHashtags.length > 0 && (
          <div className="hashtag-suggestions">
            {visibleHashtags.map((item) => (
              <button
                key={item.hashtag}
                type="button"
                className="hashtag"
                onClick={() => handleHashtagClick(item.hashtag)}
              >
                {item.hashtag}
              </button>
            ))}
            {hiddenCount > 0 && <span className="hashtag-more">+{hiddenCount} more not shown</span>}
          </div>
        )}

        {selectedHashtag && (
          <div className="search-results-header">
            <h2 className="search-results-title">
              Results for <span>{selectedHashtag}</span>
            </h2>
            <button onClick={handleClearSearch} className="clear-search-button">
              Clear search
            </button>
          </div>
        )}
      </section>

      <section className="feed-section">
        <div className="feed">
          {feedPhotos.length > 0 ? (
            feedPhotos.map((photo, index) => {
              const { isVideo, imageUrl, videoUrl, posterUrl } = getMediaUrls(photo);

              const durationSeconds =
                photo.video_duration_ms > 0 ? Math.round(photo.video_duration_ms / 1000) : null;

              return (
                <article
                  key={photo.id}
                  className="feed-photo"
                  ref={index === feedPhotos.length - 1 ? lastPhotoRef : null}
                  onClick={() => {
                    if (isVideo) return;
                    setLightboxIndex(photosForLightbox.findIndex((p) => p.id === photo.id));
                  }}
                >
                  <div className="feed-photo-media">
                    {isVideo ? (
                      <div className="feed-video-shell">
                        {videoUrl ? (
                          <video
                            src={videoUrl}
                            poster={posterUrl}
                            className="feed-photo-thumbnail feed-photo-video"
                            playsInline
                            controls
                            preload="metadata"
                            muted
                          />
                        ) : (
                          <div className="feed-missing-media">Video not available.</div>
                        )}
                        <div className="feed-video-pill">
                          <span>▶ Video</span>
                          {durationSeconds !== null && (
                            <span className="feed-video-duration">{durationSeconds}s</span>
                          )}
                        </div>
                      </div>
                    ) : imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={photo.story || "Photo"}
                        className="feed-photo-thumbnail"
                        loading="lazy"
                      />
                    ) : (
                      <div className="feed-missing-media">Image not available.</div>
                    )}
                  </div>

                  <div className="feed-photo-body">
                    <p className="feed-photo-story">{photo.story || "No story added."}</p>
                    <p className="feed-photo-meta">
                      Uploaded by {photo.uploaded_by?.replace("%20", " ") || "Unknown"} •{" "}
                      {photo.created_at ? timeAgo(photo.created_at) : "N/A"}
                      {isVideo && (
                        <>
                          {" "}• Video
                          {durationSeconds !== null && ` · ${durationSeconds}s`}
                        </>
                      )}
                    </p>
                  </div>
                </article>
              );
            })
          ) : (
            <p className="view-photos-empty">
              {isLoading ? "Loading photos..." : "No photos to display yet."}
            </p>
          )}
        </div>

        {isLoading && feedPhotos.length > 0 && (
          <p className="loading-indicator">Loading more photos…</p>
        )}
        {!hasMorePhotos && feedPhotos.length > 0 && (
          <p className="no-more-photos">You’ve reached the end of the feed.</p>
        )}
      </section>

      {lightboxIndex !== null && photosForLightbox[lightboxIndex] && (
        <Lightbox
          mainSrc={getMediaUrls(photosForLightbox[lightboxIndex]).lightboxUrl}
          nextSrc={
            photosForLightbox[(lightboxIndex + 1) % photosForLightbox.length] &&
            getMediaUrls(photosForLightbox[(lightboxIndex + 1) % photosForLightbox.length]).lightboxUrl
          }
          prevSrc={
            photosForLightbox[(lightboxIndex + photosForLightbox.length - 1) % photosForLightbox.length] &&
            getMediaUrls(
              photosForLightbox[(lightboxIndex + photosForLightbox.length - 1) % photosForLightbox.length]
            ).lightboxUrl
          }
          onCloseRequest={() => setLightboxIndex(null)}
          onMovePrevRequest={() =>
            setLightboxIndex(
              (lightboxIndex + photosForLightbox.length - 1) % photosForLightbox.length
            )
          }
          onMoveNextRequest={() =>
            setLightboxIndex((lightboxIndex + 1) % photosForLightbox.length)
          }
          imageCaption={
            (photosForLightbox[lightboxIndex].story || "") +
            " — Uploaded by " +
            (photosForLightbox[lightboxIndex].uploaded_by?.replace("%20", " ") || "Unknown")
          }
        />
      )}

      {showBackToTop && (
        <button
          className="back-to-search-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to search"
          title="Back to search"
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </button>
      )}
    </div>
  );
};

export default ViewPhotos;
